import { matchPath } from "react-router";
import { useRoutes } from "./RoutesContext";
import { useCallback } from "react";
import useIntersectionObserver from "./useIntersectionObserver";
import { useDevice } from "theme/components/helpers/MediaQuery";
import config from "config/website";

const intersectionObserverDevices =
  config && config.preload && config.preload.intersectionObserverDevices
    ? config.preload.intersectionObserverDevices
    : ["phone", "tablet"];

export function resolveComponent(loadedModule) {
  const Component = loadedModule.__esModule
    ? loadedModule.default
    : loadedModule.default || loadedModule;
  return Component;
}

const findRoutes = (routes, to) => {
  let match, route;
  let found = false;

  for (route of routes) {
    match = matchPath(to.pathname ? to.pathname : to, {
      path: route.path,
      exact: route.exact ? route.exact : false,
    });

    if (match || !route.path) {
      found = true;
      break;
    }
  }

  if (!found) {
    return [];
  }

  if (route.routes) {
    return [{ route, match }, ...findRoutes(route.routes, to)];
  } else {
    return [{ route, match }];
  }
};

export const preloadDataFromModule = (module, params) => {
  const Component = resolveComponent(module);
  return Promise.resolve()
    .then(() => {
      if (Component.preload) {
        return Component.preload(params);
      }
    })
    .catch((error) => {
      // Since this is only some preloading
      // Let's not care too much about preload errors
      // We will just log the errors for devs
      if (process.env.NODE_ENV === "development") {
        console.warn("Preloading failed", { params, reason: error });
      }
    });
};

const preloadRoute = (route, params) => {
  if (route.layout) {
    return route.layout.load().then((module) => {
      return preloadDataFromModule(module, params);
    });
  }

  if (route.component) {
    return route.component.load().then((module) => {
      return preloadDataFromModule(module, params);
    });
  }

  return Promise.resolve();
};

const usePreload = () => {
  const routes = useRoutes();

  const preload = useCallback(
    (to) => {
      const matchedRoutes = findRoutes(routes, to);
      return Promise.all(
        matchedRoutes.map(({ route, match }) => {
          return preloadRoute(route, {
            match,
            location: to.pathname ? to : { pathname: to },
          });
        })
      );
    },
    [routes]
  );

  return preload;
};

const prependFunction = (preload, fn) => {
  return (...args) => {
    preload();
    if (typeof fn === "function") {
      return fn(...args);
    }
  };
};

const toHref = (to) => {
  if (typeof to === "string") {
    return to;
  }

  return `${to.pathname}?${to.search}#${to.hash}`;
};

const usePreloadProps = ({ to, onMouseOver, onFocus }) => {
  const preload = usePreload();

  const preloadForListener = useCallback(() => {
    preload(to);
  }, [preload, to]);
  const handleRef = useIntersectionObserver({
    key: toHref(to),
    callback: preloadForListener,
  });

  const device = useDevice();
  const shouldUseIntersectionObserver = intersectionObserverDevices.includes(
    device.type
  );

  return {
    onMouseOver: prependFunction(() => preload(to), onMouseOver),
    onFocus: prependFunction(() => preload(to), onFocus),
    ref: shouldUseIntersectionObserver ? handleRef : undefined,
  };
};

export { usePreload, usePreloadProps };
